import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { AttendanceTrackingPageHeader } from 'components/molecules/AttendanceTrackingPageHeader';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { agent } from 'services/service';
import {
  AttendanceDetailDayStatus,
  AttendanceEmployeeComplianceStatus,
  EmployeeAttendanceDetailResponse
} from 'services/AttendanceTrackingService';
import {
  AttendanceDashboardPeriod,
  buildEmployeesListPath,
  buildEmployeeCalendarPath,
  formatDashboardPeriodQuery,
  formatDashboardPeriodRange,
  getLastMonthPeriod,
  isFullMonthDashboardPeriod,
  parsePeriodFromSearchParams,
  periodToSearchParams
} from 'utils/attendanceDashboardPeriod';
import { AttendanceDashboardPeriodFilter } from 'components/molecules/AttendanceDashboardPeriodFilter';
import { formatLeaveCount } from 'utils/formatAttendanceEmployee';
import {
  buildAttendanceStatusLegend,
  DEFAULT_FULL_DAY_HOURS,
  DEFAULT_SHORT_DAY_HOURS,
  getAttendanceStatusClass
} from 'utils/formatAttendanceDashboard';
import { formatAttendanceLocalDateTime } from 'utils/formatAttendanceLastImport';
import { toast } from 'react-toastify';
import './index.scss';

type DetailDayStatus = AttendanceDetailDayStatus;

interface AttendanceRecord {
  id: string;
  date: string;
  arrival: string;
  departure: string;
  hours: string;
  status: DetailDayStatus;
  isCorrected: boolean;
  correctionNote: string;
  correctedByName: string;
  correctedAt: string;
}

interface EmployeeDetail {
  epfNumber: string;
  name: string;
  department: string;
  employmentType: string;
  complianceStatus: AttendanceEmployeeComplianceStatus;
  officeDays: number;
  workingDaysInPeriod: number;
  minimumOfficeDays: number;
  leaveDays: number;
  fullDayLeaveCount: number;
  halfDayLeaveCount: number;
  missingRecords: number;
  wfhDays: number;
  fullDayHoursThreshold: number;
  shortDayHoursThreshold: number;
  records: AttendanceRecord[];
}

interface CorrectionFormState {
  date: string;
  arrival: string;
  departure: string;
  note: string;
  overrideMode: CorrectionOverrideMode | null;
}

const MANUAL_OVERRIDE_STATUSES = ['WFH', 'FullDayLeave', 'HalfDayLeave'] as const;
type ManualOverrideStatus = (typeof MANUAL_OVERRIDE_STATUSES)[number];
type CorrectionOverrideMode = 'calculated' | ManualOverrideStatus;

const OVERRIDE_MODE_LABELS: Record<CorrectionOverrideMode, string> = {
  calculated: 'From arrival & departure',
  WFH: 'WFH',
  FullDayLeave: 'Full Day Leave',
  HalfDayLeave: 'Half Day Leave'
};

const isManualOverrideStatus = (
  status: DetailDayStatus
): status is ManualOverrideStatus =>
  MANUAL_OVERRIDE_STATUSES.includes(status as ManualOverrideStatus);

const isManualOverrideMode = (
  mode: CorrectionOverrideMode
): mode is ManualOverrideStatus => mode !== 'calculated';

const hasPunchTimes = (arrival: string, departure: string) =>
  Boolean(arrival.trim() && departure.trim());

const getOverrideOptions = (
  arrival: string,
  departure: string
): CorrectionOverrideMode[] =>
  hasPunchTimes(arrival, departure)
    ? ['calculated', ...MANUAL_OVERRIDE_STATUSES]
    : [...MANUAL_OVERRIDE_STATUSES];

const normalizeOverrideMode = (
  mode: CorrectionOverrideMode | null,
  arrival: string,
  departure: string
): CorrectionOverrideMode | null => {
  const options = getOverrideOptions(arrival, departure);
  if (mode && options.includes(mode)) return mode;
  if (hasPunchTimes(arrival, departure)) return 'calculated';
  return null;
};

const computeStatusFromTimes = (
  arrival: string,
  departure: string,
  fullDayHoursThreshold = DEFAULT_FULL_DAY_HOURS,
  shortDayHoursThreshold = DEFAULT_SHORT_DAY_HOURS
): DetailDayStatus | null => {
  if (!arrival || !departure) return null;

  const [arrivalHours, arrivalMinutes] = arrival.split(':').map(Number);
  const [departureHours, departureMinutes] = departure.split(':').map(Number);

  if (
    Number.isNaN(arrivalHours) ||
    Number.isNaN(arrivalMinutes) ||
    Number.isNaN(departureHours) ||
    Number.isNaN(departureMinutes)
  ) {
    return null;
  }

  let arrivalTotalMinutes = arrivalHours * 60 + arrivalMinutes;
  let departureTotalMinutes = departureHours * 60 + departureMinutes;

  if (departureTotalMinutes < arrivalTotalMinutes) {
    departureTotalMinutes += 24 * 60;
  }

  const hoursWorked = (departureTotalMinutes - arrivalTotalMinutes) / 60;

  if (hoursWorked >= fullDayHoursThreshold) return 'Present';
  if (hoursWorked >= shortDayHoursThreshold) return 'ShortDay';
  return 'VeryShort';
};

const STATUS_CLASS: Record<DetailDayStatus, string> = {
  Present: 'present',
  ShortDay: 'short-day',
  VeryShort: 'very-short',
  Missing: 'missing',
  WFH: 'wfh',
  FullDayLeave: 'full-day-leave',
  HalfDayLeave: 'half-day-leave'
};

const DETAIL_STATUS_SHORT_LABELS: Record<DetailDayStatus, string> = {
  Present: 'Present',
  ShortDay: 'Short Day',
  VeryShort: 'Too Short',
  Missing: 'Missing',
  WFH: 'WFH',
  FullDayLeave: 'Full Day Leave',
  HalfDayLeave: 'Half Day Leave'
};

const LEGEND_WFH_ITEM = { className: 'wfh', label: 'WFH' } as const;

const DETAIL_STATUS_FILTER_VALUES = [
  'Present',
  'ShortDay',
  'VeryShort',
  'Missing',
  'WFH',
  'FullDayLeave',
  'HalfDayLeave'
] as const satisfies readonly DetailDayStatus[];

const RECORDS_PAGE_SIZE = 100;
const LOAD_MORE_THRESHOLD_PX = 120;
const recordToForm = (record: AttendanceRecord): CorrectionFormState => {
  const arrival = record.arrival === '—' ? '' : record.arrival;
  const departure = record.departure === '—' ? '' : record.departure;

  let overrideMode: CorrectionOverrideMode | null;
  if (isManualOverrideStatus(record.status)) {
    overrideMode = record.status;
  } else if (hasPunchTimes(arrival, departure)) {
    overrideMode = 'calculated';
  } else {
    overrideMode = null;
  }

  return {
    date: record.date,
    arrival,
    departure,
    note: record.correctionNote,
    overrideMode: normalizeOverrideMode(overrideMode, arrival, departure)
  };
};

// Fallback helper removed — not used in production code

export const AttendanceEmployeeDetail = () => {
  const navigate = useNavigate();
  const { epfNumber = '' } = useParams<{ epfNumber: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [employee, setEmployee] = useState<EmployeeDetail | null>(null);
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | DetailDayStatus>(
    'all'
  );
  const [period, setPeriod] = useState<AttendanceDashboardPeriod>(() =>
    parsePeriodFromSearchParams(searchParams, getLastMonthPeriod())
  );
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [formState, setFormState] = useState<CorrectionFormState | null>(null);
  const [originalFormState, setOriginalFormState] =
    useState<CorrectionFormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageRef = useRef(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLTableRowElement>(null);
  const onLoadMoreRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    setStatusFilter('all');
    setPeriod(parsePeriodFromSearchParams(searchParams, getLastMonthPeriod()));
  }, [epfNumber, searchParams]);

  const handlePeriodChange = useCallback(
    (nextPeriod: AttendanceDashboardPeriod) => {
      setPeriod(nextPeriod);
      setSearchParams(periodToSearchParams(nextPeriod), { replace: true });
    },
    [setSearchParams]
  );

  const employeesListPath = useMemo(
    () => buildEmployeesListPath(period),
    [period]
  );

  const mapRecords = (
    response: EmployeeAttendanceDetailResponse
  ): AttendanceRecord[] =>
    response.records.map((r) => ({
      id: `${response.epfNumber}-${r.date}`,
      date: r.date,
      arrival: r.arrivalTime ? r.arrivalTime.slice(0, 5) : '—',
      departure: r.departureTime ? r.departureTime.slice(0, 5) : '—',
      hours: r.hoursWorked ?? '—',
      status: r.status,
      isCorrected: r.isCorrected,
      correctionNote: r.correctionNote?.trim() ?? '',
      correctedByName: r.correctedByName?.trim() ?? '',
      correctedAt: r.correctedAt ?? ''
    }));

  const mapEmployeeDetail = (
    response: EmployeeAttendanceDetailResponse
  ): EmployeeDetail => ({
    epfNumber: response.epfNumber,
    name: response.employeeFullName,
    department: response.department ?? '—',
    employmentType: response.employmentType ?? '—',
    complianceStatus: response.isCompliant ? 'Compliant' : 'BelowThreshold',
    officeDays: response.officeDays,
    workingDaysInPeriod:
      response.workingDaysInPeriod ?? response.totalRecords,
    minimumOfficeDays: response.requiredOfficeDays,
    leaveDays: response.leaveDays,
    fullDayLeaveCount: response.fullDayLeaveCount ?? 0,
    halfDayLeaveCount: response.halfDayLeaveCount ?? 0,
    missingRecords: response.missingRecords,
    wfhDays: response.wfhDays,
    fullDayHoursThreshold:
      response.fullDayHoursThreshold ?? DEFAULT_FULL_DAY_HOURS,
    shortDayHoursThreshold:
      response.shortDayHoursThreshold ?? DEFAULT_SHORT_DAY_HOURS,
    records: mapRecords(response)
  });

  const fetchDetail = useCallback(
    async (pageToFetch: number, append: boolean) => {
      const { from, to } = formatDashboardPeriodQuery(period);
      const response =
        await agent.AttendanceTrackingService.getEmployeeAttendanceDetail(
          epfNumber,
          from,
          to,
          undefined,
          pageToFetch,
          RECORDS_PAGE_SIZE
        );
      const mappedRecords = mapRecords(response.data);

      if (append) {
        setRecords((current) => [...current, ...mappedRecords]);
      } else {
        setEmployee(mapEmployeeDetail(response.data));
        setRecords(mappedRecords);
      }

      setTotalRecords(response.data.totalRecords);
      pageRef.current = response.data.page;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [epfNumber, period]
  );

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    pageRef.current = 1;

    try {
      await fetchDetail(1, false);
    } catch {
      setEmployee(null);
      setRecords([]);
      setTotalRecords(0);
      setError('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  }, [fetchDetail]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore) return;
    if (records.length >= totalRecords) return;

    setLoadingMore(true);

    try {
      await fetchDetail(pageRef.current + 1, true);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchDetail, loading, loadingMore, records.length, totalRecords]);

  onLoadMoreRef.current = loadMore;

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  const hasMore = records.length < totalRecords;

  const filteredRecords = useMemo(() => {
    if (statusFilter === 'all') return records;
    return records.filter((record) => record.status === statusFilter);
  }, [records, statusFilter]);

  const selectRecord = useCallback((record: AttendanceRecord) => {
    const form = recordToForm(record);
    setSelectedRecordId(record.id);
    setFormState(form);
    setOriginalFormState(form);
  }, []);

  useEffect(() => {
    if (filteredRecords.length === 0) return;
    const stillVisible = filteredRecords.some((r) => r.id === selectedRecordId);
    if (!selectedRecordId || !stillVisible) selectRecord(filteredRecords[0]);
  }, [filteredRecords, selectedRecordId, selectRecord]);

  const shouldLoadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore) return false;

    const root = scrollRef.current;
    if (!root) return false;

    const distanceFromBottom =
      root.scrollHeight - root.scrollTop - root.clientHeight;

    return distanceFromBottom <= LOAD_MORE_THRESHOLD_PX;
  }, [hasMore, loading, loadingMore]);

  const tryLoadMore = useCallback(() => {
    if (shouldLoadMore()) {
      onLoadMoreRef.current();
    }
  }, [shouldLoadMore]);

  const fillScrollContainerIfNeeded = useCallback(() => {
    const root = scrollRef.current;
    if (!root || !hasMore || loading || loadingMore) return;

    if (root.scrollHeight <= root.clientHeight + 4) {
      onLoadMoreRef.current();
    }
  }, [hasMore, loading, loadingMore]);

  useEffect(() => {
    const root = scrollRef.current;
    const target = loadMoreRef.current;

    if (!root || !target || !hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onLoadMoreRef.current();
        }
      },
      {
        root,
        rootMargin: `${LOAD_MORE_THRESHOLD_PX}px`,
        threshold: 0
      }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, records.length]);

  useEffect(() => {
    if (loading) return;

    let cancelled = false;

    const runLayoutChecks = () => {
      if (cancelled) return;
      tryLoadMore();
      fillScrollContainerIfNeeded();
    };

    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(runLayoutChecks);
    });

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' && scrollRef.current
        ? new ResizeObserver(runLayoutChecks)
        : null;

    if (resizeObserver && scrollRef.current) {
      resizeObserver.observe(scrollRef.current);
    }

    const layoutTimer = window.setTimeout(runLayoutChecks, 300);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      clearTimeout(layoutTimer);
      resizeObserver?.disconnect();
    };
  }, [
    loading,
    loadingMore,
    hasMore,
    records.length,
    tryLoadMore,
    fillScrollContainerIfNeeded
  ]);

  const handleTableScroll = useCallback(() => {
    tryLoadMore();
  }, [tryLoadMore]);

  const selectedRecord = useMemo(
    () => records.find((record) => record.id === selectedRecordId) ?? null,
    [records, selectedRecordId]
  );

  const handleClearForm = () => {
    if (originalFormState) setFormState({ ...originalFormState });
  };

  const handleSaveCorrection = async () => {
    if (!formState || !employee) return;

    const punchRecorded = hasPunchTimes(formState.arrival, formState.departure);

    if (!punchRecorded) {
      if (!formState.overrideMode || !isManualOverrideMode(formState.overrideMode)) {
        toast.info('Choose WFH, Half Day Leave, or Full Day Leave.');
        return;
      }
    } else if (
      formState.overrideMode === 'calculated' &&
      (!formState.arrival || !formState.departure)
    ) {
      toast.info('Enter arrival and departure times, or choose a manual status.');
      return;
    }

    setSaving(true);
    try {
      await agent.AttendanceTrackingService.saveAttendanceCorrection(
        employee.epfNumber,
        {
          date: formState.date,
          arrivalTime: formState.arrival,
          departureTime: formState.departure,
          correctionNote: formState.note,
          overrideStatus:
            formState.overrideMode === 'calculated' ||
            !formState.overrideMode
              ? undefined
              : formState.overrideMode
        }
      );
      toast.success('Correction saved.');
      await loadInitial();
    } catch {
      toast.error('Failed to save correction.');
    } finally {
      setSaving(false);
    }
  };

  const fullDayHours =
    employee?.fullDayHoursThreshold ?? DEFAULT_FULL_DAY_HOURS;
  const shortDayHours =
    employee?.shortDayHoursThreshold ?? DEFAULT_SHORT_DAY_HOURS;

  const statusFilterOptions = useMemo(
    () => [
      { value: 'all' as const, label: 'All statuses' },
      ...DETAIL_STATUS_FILTER_VALUES.map((value) => ({
        value,
        label: DETAIL_STATUS_SHORT_LABELS[value]
      }))
    ],
    []
  );

  const renderStatusBadge = (status: DetailDayStatus) => (
    <span
      className={`attendance-dashboard__status-text attendance-dashboard__status-text--${STATUS_CLASS[status]}`}
    >
      {DETAIL_STATUS_SHORT_LABELS[status]}
    </span>
  );

  const renderRecordStatus = (record: AttendanceRecord) => (
    <div className="attendance-employee-detail-page__status-cell">
      {renderStatusBadge(record.status)}
      {record.isCorrected ? (
        <span className="attendance-employee-detail-page__corrected-label">
          Corrected
        </span>
      ) : null}
    </div>
  );

  const statusLegend = useMemo(() => {
    const hourBased = buildAttendanceStatusLegend(fullDayHours, shortDayHours);
    return [
      ...hourBased.map((item) => ({
        className: getAttendanceStatusClass(item.status),
        label: item.label
      })),
      LEGEND_WFH_ITEM
    ];
  }, [fullDayHours, shortDayHours]);

  const calculatedFormStatus = useMemo(() => {
    if (!formState || formState.overrideMode !== 'calculated') return null;
    return computeStatusFromTimes(
      formState.arrival,
      formState.departure,
      fullDayHours,
      shortDayHours
    );
  }, [formState, fullDayHours, shortDayHours]);

  const overrideOptions = useMemo(
    () =>
      formState
        ? getOverrideOptions(formState.arrival, formState.departure)
        : [...MANUAL_OVERRIDE_STATUSES],
    [formState]
  );

  const effectiveFormStatus: DetailDayStatus | null = useMemo(() => {
    if (!formState) return null;

    if (formState.overrideMode === 'calculated') {
      return calculatedFormStatus;
    }

    if (formState.overrideMode && isManualOverrideMode(formState.overrideMode)) {
      return formState.overrideMode;
    }

    if (!hasPunchTimes(formState.arrival, formState.departure)) {
      return 'Missing';
    }

    return calculatedFormStatus;
  }, [formState, calculatedFormStatus]);

  if (loading) {
    return (
      <div className="attendance-employee-detail-page">
        <div className="attendance-employee-detail-page__not-found">
          Loading employee attendance...
        </div>
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="attendance-employee-detail-page">
        <div className="attendance-employee-detail-page__not-found">
          {error ?? 'Employee not found.'}
        </div>
      </div>
    );
  }

  const isCompliant = employee.complianceStatus === 'Compliant';
  const isFullMonth = isFullMonthDashboardPeriod(period);
  const officeDayProgress = Math.min(
    100,
    employee.minimumOfficeDays > 0
      ? (employee.officeDays / employee.minimumOfficeDays) * 100
      : 0
  );

  return (
    <div className="attendance-employee-detail-page">
      <AttendanceTrackingPageHeader
        title={employee.name}
        description={`${employee.department} · EPF ${employee.epfNumber} · ${employee.employmentType}`}
        descriptionSingleLine
        subDescription={`Showing data for ${formatDashboardPeriodRange(period)}`}
        titleLeading={
          <button
            type="button"
            className="attendance-employee-detail-page__back-icon-btn"
            aria-label="Back to Employee Attendance"
            onClick={() => navigate(employeesListPath)}
          >
            <ChevronLeftIcon fontSize="small" aria-hidden />
          </button>
        }
        titleAction={
          <button
            type="button"
            className="attendance-employee-detail-page__calendar-btn"
            onClick={() =>
              navigate(buildEmployeeCalendarPath(employee.epfNumber, period))
            }
          >
            <CalendarMonthOutlinedIcon fontSize="small" aria-hidden />
            View Calendar
          </button>
        }
        crumbs={[
          { label: 'Attendance Tracking', to: '/attendance-tracking' },
          {
            label: 'Employee Attendance',
            to: employeesListPath
          },
          { label: employee.name }
        ]}
      />

      {isFullMonth && (
        <div
          className={`attendance-employee-detail-page__compliance-banner attendance-employee-detail-page__compliance-banner--${isCompliant ? 'compliant' : 'below'}`}
        >
            <div className="attendance-employee-detail-page__compliance-icon">
              {isCompliant ? (
                <CheckCircleOutlineIcon fontSize="inherit" aria-hidden />
              ) : (
                <HighlightOffOutlinedIcon fontSize="inherit" aria-hidden />
              )}
            </div>
            <div className="attendance-employee-detail-page__compliance-body">
              <p className="attendance-employee-detail-page__compliance-line">
                <span className="attendance-employee-detail-page__compliance-title">
                  {isCompliant ? 'Compliant' : 'Below threshold'}
                </span>
                {' — '}
                <strong>{employee.name}</strong> has completed{' '}
                <strong>{employee.officeDays}</strong> office{' '}
                {employee.officeDays === 1 ? 'day' : 'days'} · Minimum
                required: <strong>{employee.minimumOfficeDays} days</strong>
              </p>
              <div
                className="attendance-employee-detail-page__compliance-progress"
                role="progressbar"
                aria-valuenow={employee.officeDays}
                aria-valuemin={0}
                aria-valuemax={employee.minimumOfficeDays}
                aria-label={`Office days progress: ${employee.officeDays} of ${employee.minimumOfficeDays}`}
              >
                <div
                  className="attendance-employee-detail-page__compliance-progress-bar"
                  style={{ width: `${officeDayProgress}%` }}
                />
              </div>
            </div>
          </div>
      )}

      <div className="attendance-employee-detail-page__metrics">
        <div
          className={`attendance-employee-detail-page__metric attendance-employee-detail-page__metric--office-${isCompliant ? 'ok' : 'low'}`}
        >
          <span className="attendance-employee-detail-page__metric-label">
            Work from Office Days
          </span>
          <span className="attendance-employee-detail-page__metric-value">
            {employee.officeDays}/{employee.workingDaysInPeriod}
          </span>
        </div>
        <div className="attendance-employee-detail-page__metric attendance-employee-detail-page__metric--full-leave">
          <span className="attendance-employee-detail-page__metric-label">
            Full Day Leave
          </span>
          <span className="attendance-employee-detail-page__metric-value">
            {formatLeaveCount(employee.fullDayLeaveCount)}
          </span>
        </div>
        <div className="attendance-employee-detail-page__metric attendance-employee-detail-page__metric--half-leave">
          <span className="attendance-employee-detail-page__metric-label">
            Half Day Leave
          </span>
          <span className="attendance-employee-detail-page__metric-value">
            {formatLeaveCount(employee.halfDayLeaveCount)}
          </span>
        </div>
        <div className="attendance-employee-detail-page__metric attendance-employee-detail-page__metric--missing">
          <span className="attendance-employee-detail-page__metric-label">
            Missing Records
          </span>
          <span className="attendance-employee-detail-page__metric-value">
            {employee.missingRecords}
          </span>
        </div>
        <div className="attendance-employee-detail-page__metric attendance-employee-detail-page__metric--wfh">
          <span className="attendance-employee-detail-page__metric-label">
            WFH Days
          </span>
          <span className="attendance-employee-detail-page__metric-value">
            {employee.wfhDays}
          </span>
        </div>
      </div>

      <div className="attendance-employee-detail-page__layout">
        <section className="attendance-employee-detail-page__panel">
          <h2 className="attendance-employee-detail-page__panel-title">
            Daily Records
          </h2>

          {/* LEGEND — moved to top */}
          <div className="attendance-employee-detail-page__legend">
            {statusLegend.map((item) => (
              <span
                key={item.className}
                className={`attendance-employee-detail-page__legend-item attendance-employee-detail-page__legend-item--${item.className}`}
              >
                {item.label}
              </span>
            ))}
          </div>

          {/* FILTERS — Status stays left, date picker pushed to far right */}
          <div className="attendance-employee-detail-page__filters">
            <label className="attendance-employee-detail-page__filter-label">
              Status
              <select
                className="attendance-employee-detail-page__select"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value as 'all' | DetailDayStatus);
                }}
              >
                {statusFilterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {/* PERIOD PICKER — same as dashboard, pushed right via wrapper */}
            <div className="attendance-employee-detail-page__filter-spacer">
              <AttendanceDashboardPeriodFilter
                period={period}
                onChange={handlePeriodChange}
              />
            </div>
          </div>

          <div
            ref={scrollRef}
            className="attendance-employee-detail-page__table-wrap"
            onScroll={handleTableScroll}
          >
            <table className="attendance-employee-detail-page__table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Arrival</th>
                  <th>Departure</th>
                  <th>Hours</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.length > 0 ? (
                  <>
                    {filteredRecords.map((record) => (
                      <tr
                        key={record.id}
                        className={
                          record.id === selectedRecordId
                            ? 'attendance-employee-detail-page__table-row--selected'
                            : undefined
                        }
                        onClick={() => selectRecord(record)}
                      >
                        <td>{record.date}</td>
                        <td>{record.arrival}</td>
                        <td>{record.departure}</td>
                        <td>{record.hours}</td>
                        <td>{renderRecordStatus(record)}</td>
                      </tr>
                    ))}
                    {hasMore && statusFilter === 'all' && (
                      <tr
                        ref={loadMoreRef}
                        className="attendance-employee-detail-page__load-more-row"
                      >
                        <td colSpan={5}>
                          <span className="attendance-employee-detail-page__load-more">
                            {loadingMore ? (
                              <>
                                <CircularProgress size={18} />
                                Loading more records...
                              </>
                            ) : (
                              'Scroll for more'
                            )}
                          </span>
                        </td>
                      </tr>
                    )}
                  </>
                ) : (
                  <tr>
                    <td colSpan={5}>
                      {records.length > 0
                        ? 'No records match this status.'
                        : 'No records for this period.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <footer className="attendance-employee-detail-page__footer">
            <span className="attendance-employee-detail-page__footer-meta">
              {totalRecords === 0
                ? 'Showing 0 records'
                : statusFilter === 'all'
                  ? `Showing ${records.length} of ${totalRecords} records`
                  : `Showing ${filteredRecords.length} of ${records.length} records (${DETAIL_STATUS_SHORT_LABELS[statusFilter]})`}
            </span>
          </footer>
        </section>

        <section className="attendance-employee-detail-page__panel">
          <div className="attendance-employee-detail-page__panel-header">
            <h2 className="attendance-employee-detail-page__panel-title">
              Add / Correct Record
            </h2>
          </div>

          {formState && (
            <form
              id="correction-form"
              className="attendance-employee-detail-page__form"
              onSubmit={(event) => {
                event.preventDefault();
                handleSaveCorrection();
              }}
            >
              <div className="attendance-employee-detail-page__field">
                <label
                  className="attendance-employee-detail-page__field-label"
                  htmlFor="correction-date"
                >
                  Date
                </label>
                <input
                  id="correction-date"
                  type="text"
                  readOnly
                  className="attendance-employee-detail-page__field-input attendance-employee-detail-page__field-input--readonly"
                  value={formState.date}
                />
              </div>
              <div className="attendance-employee-detail-page__field">
                <label
                  className="attendance-employee-detail-page__field-label"
                  htmlFor="correction-arrival"
                >
                  Arrival Time
                </label>
                <input
                  id="correction-arrival"
                  type="time"
                  className="attendance-employee-detail-page__field-input"
                  value={formState.arrival}
                  onChange={(e) =>
                    setFormState((c) => {
                      if (!c) return c;
                      const arrival = e.target.value;
                      return {
                        ...c,
                        arrival,
                        overrideMode: normalizeOverrideMode(
                          c.overrideMode,
                          arrival,
                          c.departure
                        )
                      };
                    })
                  }
                />
              </div>
              <div className="attendance-employee-detail-page__field">
                <label
                  className="attendance-employee-detail-page__field-label"
                  htmlFor="correction-departure"
                >
                  Departure Time
                </label>
                <input
                  id="correction-departure"
                  type="time"
                  className="attendance-employee-detail-page__field-input"
                  value={formState.departure}
                  onChange={(e) =>
                    setFormState((c) => {
                      if (!c) return c;
                      const departure = e.target.value;
                      return {
                        ...c,
                        departure,
                        overrideMode: normalizeOverrideMode(
                          c.overrideMode,
                          c.arrival,
                          departure
                        )
                      };
                    })
                  }
                />
              </div>
              <div className="attendance-employee-detail-page__field">
                <label
                  className="attendance-employee-detail-page__field-label"
                  htmlFor="correction-note"
                >
                  Correction Note
                </label>
                <textarea
                  id="correction-note"
                  className="attendance-employee-detail-page__field-textarea"
                  value={formState.note}
                  onChange={(e) =>
                    setFormState((c) =>
                      c ? { ...c, note: e.target.value } : c
                    )
                  }
                />
                {selectedRecord?.isCorrected &&
                (selectedRecord.correctedByName || selectedRecord.correctedAt) ? (
                  <p className="attendance-employee-detail-page__correction-meta">
                    Last saved
                    {selectedRecord.correctedByName
                      ? ` by ${selectedRecord.correctedByName}`
                      : ''}
                    {selectedRecord.correctedAt
                      ? ` on ${formatAttendanceLocalDateTime(selectedRecord.correctedAt) ?? selectedRecord.correctedAt}`
                      : ''}
                    .
                  </p>
                ) : null}
              </div>
              <div className="attendance-employee-detail-page__field">
                <span className="attendance-employee-detail-page__field-label">
                  Status
                </span>
                <div className="attendance-employee-detail-page__computed-status">
                  {effectiveFormStatus ? (
                    renderStatusBadge(effectiveFormStatus)
                  ) : (
                    <span className="attendance-employee-detail-page__computed-status-placeholder">
                      Enter arrival and departure to calculate
                    </span>
                  )}
                </div>
              </div>
              <div className="attendance-employee-detail-page__field">
                <label
                  className="attendance-employee-detail-page__field-label"
                  htmlFor="correction-status"
                >
                  Override Status
                </label>
                <select
                  id="correction-status"
                  className="attendance-employee-detail-page__field-select"
                  value={formState.overrideMode ?? ''}
                  onChange={(e) =>
                    setFormState((c) => {
                      if (!c) return c;
                      const nextValue = e.target.value;
                      return {
                        ...c,
                        overrideMode: nextValue
                          ? (nextValue as CorrectionOverrideMode)
                          : null
                      };
                    })
                  }
                >
                  {!formState.overrideMode ? (
                    <option value="" disabled>
                      Select status
                    </option>
                  ) : null}
                  {overrideOptions.map((mode) => (
                    <option key={mode} value={mode}>
                      {OVERRIDE_MODE_LABELS[mode]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="attendance-employee-detail-page__form-actions">
                <button
                  type="button"
                  className="attendance-employee-detail-page__btn attendance-employee-detail-page__btn--ghost"
                  onClick={handleClearForm}
                >
                  Clear
                </button>
                <button
                  type="submit"
                  className="attendance-employee-detail-page__btn attendance-employee-detail-page__btn--primary"
                  disabled={saving}
                >
                  Save Correction
                </button>
              </div>
            </form>
          )}
        </section>
      </div>
    </div>
  );
};
